import { IMapLayer, IMapLayerIcon } from "../map/types";

/**
 * IBase
 * consists of common properties of many other classes. it provides basic data and public contact info
 */

export const STAGE_COLOR:IStageColor = {
  Draft: '#2E86C1', //blue
  New: '#E74C3C', //red
  Refused: '#85929E', //grey
  Accepted: '#E67E22', //orange
  Implementing: '#FAE007', //yellow
  Done: '#96FA07', //yellow-green
  Closed: '#07B321' //grey || green
}

export interface IExif {
  orientation?: number;
  rotation?: number;
  gps?: {
    longitude?: number;
    longitudeRef?: string;
    latitude?: number;
    latitudeRef?: string;
    altitude?: number;
    altitudeRef?: string;
  };
}

export interface IStageColor {
  Draft?: string;
  New?: string;
  Refused?: string;
  Accepted?: string;
  Implementing?: string;
  Done?: string;
  Closed?: string;
}

export interface IBase {
  HalloId?: string; //system generated surrogate key.
  Code?: string; //application defined key, define once and never changed
  Name?: string; //display name, such as nickname or short name
  Catchword?: string;  //a message describing characteritics and an entity
  Avatar?: string;
  CoverImage?: string;
  Region?: ICountry;
  Email?: string;
  Mobile?: string;
  QRCode?: string;

  ListIndex?: number; //used in listview for display purpose
}

/**
 * IPerson
 * contains personal info for general identification
 */
export interface IPerson extends IBase {
  Prefix?: string;
  GivenName?: string;
  Surname?: string;
  Gender?: 'male' | 'female';
}
/**
 * IPersonPrivacy
 * holds top privacy of a person
 */
export interface IPersonPrivacy extends IPerson {
  NationalId?: string;
  PassportNo?: string;
  Birthday?: Date;
  Address?: IAddress;
}
/**
 * IEmployee
 * is an IPersonPrivacy who associates to an IOrganization and IPosition
 */
export interface IEmployee extends IPersonPrivacy {
  Organization?: IOrganization;
  Position?: IPosition;
}
/**
 * IUser
 * is privilege IEmployee who holds secure account info for an application
 */
export interface IUser extends IEmployee {
  UserHalloId?: string;
  UserName?: string;
  Password?: string;
  ApiKey?: string;
  Group?: EUserGroup;
  Status?: EUserStatus;
  TeamCode?: string;
  RegisterDate?: Date;
  ResponsibleArea?: IAdminArea;
  ResponsibleAreaCode?: string;
  Facebook?: string;
  Twitter?: string;
  Google?: string;
  Settings?: ISettings;
  Token?: IUserToken;
  
  loginStatus?: number;
}

/**
 * a global state for tracking user privilege
 * it should be set after user login()
 */
export interface IUserStateXX {
  HalloId?: string;
  Avatar?: string;
  FullName?: string;
  //GivenName: string;
  //Surname?: string;
  Nickname?: string;
  Organization?: IOrganization;
  Group: EUserGroup;
  Token?: IUserToken;
  Language?: string;
}

// global store / variables for Vue
export interface IVueStateXX {
  //User: IUserState;
  User: IUser;
  Page: string;
}

export interface IUserToken {
  hallo?: string; //used to login to websocket
  access?: {
    token: string;
    expire?: number;
  };
  refresh?: {
    token: string;
    expire?: number;
  };
}
export interface IOTP {
  otp: string;
  expire: number;
  key?: string;
}

/**
 * IContact
 * represents the IBase of IPerson, IPersonPrivacy, IEmployee and IUser
 * all of them have the same HalloId
 */
export interface IContact extends IPerson {
  Status?: EContactStatus;
}
/**
 * ITeam
 * holds a group of IContact. it also owns IChannel(s) for communication
 */
export interface ITeam extends IBase {
  Type?: ETeamType;
  Upline?: IContact; //the user who creates ITeam, or later assigned by previous Upline
  Members?: IContact[]; //only one contact if this.ETeamType==Individual
  Channels?: IChannel[]; //an ITeam may have one or many channels
}

/**
 * IChannels
 * contains all IChannel, separated by EChannelType and EContactStatus suitable for display purpose
 */
export interface IChannels {
  All: IChannel[];
  Invited: IChannel[];
  Official: IChannel[]; //Public or Private channels that belong to a Opened team
  Group: IChannel[]; //Direct with more than 1 other member
  Individual: IChannel[]; //Direct with only one other member
};
/**
 * IChannel
 * is a chat topic belonged to an ITeam
 * a Channel contains at least one IContact, otherwise, the Channel should have died
 */
export interface IChannel extends IBase {
  Type?: EChannelType;
  Team?: ITeam; //ITem to whom this channel belongs
  Contacts?: IContact[];
  Messages?: IMessage[];
  LastMessage?: IMessage; //i.e. IMessages[IMessages.length - 1]
  Unread?: number; //total number of messages having Read==0
}

/**
 * IMessage 
 * is the information sent and received in an IChannel
 */
export interface IMessage {
  HalloId: string;
  Type: EMessageType;
  Category?: ENotificationType;
  Sender: IPerson;
  DateSent: Date;
  Text?: string;
  SourceUri?: string;
  Location?: ILocation;
  File?: IFile;
  Read?: number;
  DateRead?: Date; //date of last read
  Channel?: IChannel; //to which the message belongs 
}

/**
 * IAdminArea
 * the abstract representation of hierarchical administrative area
 */
export interface IAdminArea {
  Code?: string;
  Name?: string;
  SubArea?: IAdminArea;
}
/**
 * ITambon
 * the lowest level of IAdminArea, i.e. its SubArea is null
 */
export interface ITambon extends IAdminArea {
}
/**
 * IAmphoe
 * the upline of ITambon. i.e. its SubArea == Tambon
 */
export interface IAmphoe extends IAdminArea {
  Tambon?: ITambon;
}
/**
 * IProvince
 * the upline of IAmphoe. i.e. its SubArea == Amphoe
 */
export interface IProvince extends IAdminArea {
  Amphoe?: IAmphoe
}
/**
 * ICountry
 * the upline of IProvince. i.e. its SubArea == Province
 */
export interface ICountry extends IAdminArea {
  Province?: IProvince;
  IDC?: string; //International Dial Code
}
/**
 * ILocation
 * represent a geographic location that can be shown on map
 */
export interface ILocation {
  Longitude?: number;
  Latitude?: number;
  Name?: string;
  Vicinity?: string;
  Area?: IAdminArea;
}
/**
 * IAddress
 * the representation of tradictional mailling address
 */
export interface IAddress {
  AddressNo?: string;
  Moo?: string;
  Alley?: string;
  Street?: string;
  Village?: string;
  Town?: string;
  County?: string;
  Province?: string;
  Country?: string;
  ZipCode?: string;
}

export interface IThemeData {
  title?: string;
  subtitle?: string;
  color?: string;
  background?: string;
  image?: string;
  file?: IUploadFile;
}

export interface ITheme {
  logo?: IThemeData;
  splash?: IThemeData;
  banner?: IThemeData;
  login?: IThemeData;
  calendar?: IThemeData;
  actionbar?: IThemeData;
  navbar?: IThemeData;
  menubar?: IThemeData;
  appMenus?: IThemeData[];
  listMenus?: IThemeData;
  wallpaper?: IThemeData;
  video?: IThemeData;
}

/**
 * IPosition
 * the node in an org chart. it is also the basic unit of an IOrganization
 */
export interface IPosition extends IBase {
  Employee?: IEmployee;
  Subperior?: IUser;
  Upline?: IPosition;
  Downlines?: IPosition[];
  //for ui control
  vertical?: boolean;
  hidden?: boolean;
  showChild?: boolean;
}
/**
 * IOrganization
 * is the root holding all IPosition of an organization. it grows down forming 
 * a tree structure of IPosition, connected via Superordinate and 
 * a group of Subordinates, which represents the hierarchical responsibilities
 */
export interface IOrganization extends IBase {
  Abbr?: string;
  Manager?: IPosition;
  Upline?: IOrganization;
  Downlines?: IOrganization[];
  Address?: IAddress;
}
/**
 * IServiceProvider
 * another representation of IOrganization for service. they have the same IBase or HalloId
 * -- to adapt the team structure for storing data
 */
export interface IServiceProvider extends IBase {
  Url?: string;
  ServiceCatalogs?: IServiceCatalog[];
};

/**
 * IServiceCatalog
 * the type of services provided by an IServiceProvider
 * -- to adapt the channel structure for storing data
 */
export interface IServiceCatalog extends IBase {
  Available?:boolean;
  Manager?:IUser;
  ServiceProvider?: IServiceProvider; //required if this is the root of Catalogs
  Upline?: IServiceCatalog; //required if this is a child of the Catalog
  Downlines?: IServiceCatalog[]; //sub-catalogs

  showDownlines?:boolean; //used to control visibility of Downlines
};

/**
 * ITransactionDates
 * dates created by server for a transaction committed to database
 */
export interface ITransactionDates {
  Created?: Date;
  Updated?: Date;
  Completed?: Date;
  Deleted?: Date;
}
/**
 * IParagraph
 * the basic contents for a IRequest
 */
export interface IParagraph {
  HalloId?: string;
  Type?: EParagraphType;
  Text?: string;
  SourceUri?: string;
  Dates?: ITransactionDates;
  Exif?: IExif;

  loadingPhoto?:boolean;
}
/**
 * IComment
 * comment on an IRequest by an IUser
 */
export interface IComment {
  HalloId?: string;
  Commentor?: IUser;
  Date?: Date;
  Text?: string;
}
export interface ISearchParams {
  searchNewer?: boolean;
  searchOlder?: boolean;
  search?: string;
  offset?: number;
  limit?: number;
}
/**
 * IRequestDates
 * dates of an IRequest at different status
 * they will be set/updated on server
 */
export interface IRequestDates {  //
  Draft?: Date,  //this date will not be stored on server. it will be replaced by Sent Date once it is sent to server
  Sent?: Date,
  Updated?: Date, //updated after sent, can be done by owner only
  Returned?: Date,
  Refused?: Date,
  Accepted?: Date,
  Assigned?: Date,
  Preparing?: Date,
  Implementing?: Date,
  Done?: Date,
  Closed?: Date
}
/**
 * IRequest
 * holds the main data contents that flows along the specific work flow
 */
export interface IRequest {
  HalloId?: string;
  RequestNo?: string;
  Title?: string;
  ServiceProviderCode?: string; //organization -> department -> team
  ServiceCategoryCode?: string; //organization -> department -> team
  Owner?: IUser;
  Intention?: EIntention;
  Reason?: ERequestReason;
  ReasonDesc?: string;
  Privacy?: ERequestPrivacy;
  Status?: ERequestStatus;
  Dates?: IRequestDates;
  NumberOfView?: number;
  NumberOfLike?: number;
  NumberOfComment?: number; //total comments from server
  CoverUri?: string;
  Location?: ILocation;
  Contents?: IParagraph[];
  Comments?: IComment[];
  Jobs?: IJob[];
}

export interface IAoi {
  HalloId?: string; //gid
  OwnerId?: string;  //user_fk
  AoiNo?: string; //aoi_no
  Name?: string; //name_th
  Description?: string; //detail_th
  StartDate?: Date;
  Periods?: number;
  AreaSqm?: number;
  AreaRai?: number;
  AreaNgan?: number;
  AreaSqwah?: number;
  Contracts?: number;
  DataMb?: number;
  UnitPrice?: number;
  PriceTotal?: number;
  Status?: number;
  Dates?: ITransactionDates; //create_ts,update_ts,delete_ts,complete_ts
}

export interface IDateYMD {
  year: number;
  month: number;
  day: number;
}
export interface IDateRange {
  beginDate: IDateYMD;
  endDate: IDateYMD;
}
export interface IJob {
  HalloId?: string;
  Name?: string;
  DateBegin?: Date;
  DateEnd?: Date;
  DateRange?: IDateRange;
  DateCompleted?: Date;
  Assignee?: IUser[];
  Results?: IParagraph[];
}

export interface ISticker {
  Type?: EStickType;
  Name?: string;
}

export interface ISettings {
  //INotification;
  NotifyMessage?: boolean;
  NotifyVideoCall?: boolean;
  ShowPreviewText?: boolean;
  EnableAlertSound?: boolean;
  EnableVibrate?: boolean;
  EnableNightMode?: boolean;
  //IPrivacy;
  InvitationRequireConfirmation?: boolean;
  AllowFindMeByPhone?: boolean;
  AllowFindMeByHalloId?: boolean;
  AllowFindMeByEmail?: boolean;
  AllowAddMeByGroupChat?: boolean;
  AllowAddMeByQRCode?: boolean;
  AllowAddMeByContactCard?: boolean;
  AllowFindMyRequestMonth?: number;
  BlockedLists?: IContact[];
  //IGeneral
  Language?: string;
  TextSize?: number;
  Background?: string; //src of backgrround-image for chat view etc.
  AutoSavePhoto?: boolean;
  AutoSaveVideo?: boolean;
  Stickers?: ISticker[];
  TurnOffSpeaker?: boolean;
}

export interface IDataCount {
  Teams?: number;
  Requests?: number;
  Chats?: number;
  News?: number;
}

// allows to new Image()
export interface Image {
  prototype: HTMLImageElement;
  new (): HTMLImageElement;
}

export interface IDialog {
  show?: boolean;
  wait?: boolean;
  Type?: "info" | "error" | "alert" | "login" | "form" | "calendar";
  OkText?: "ok" | "yes" | "submit" | null;
  CancelText?: "cancel" | "close" | "no" | null;
  DisableOk?: boolean;
  Title?: string | string[];
  Message?: string | string[] | {[key:string]:any}[];
  OnClose?: Function;
}

export interface IFile {
  HalloId?: string;
  Name?: string;
  Size?: string;
}

export interface ISize {
  width: number;
  height: number;
}

export interface IUploadParams {
  filePath: string, //required
  fieldName?: string, //column name where the image path will be stored
  details?: any //additional field data
}

export interface INotification {

}

export interface INews extends IBase {
  DateStart?: Date;
  DateEnd?: Date;
  Contents?: IParagraph[];
}

export interface IEdit {
  Code: "PHOTO" | "SERVICE" | "LOCATION";
  Name: string;
  Text?: string;
}
export interface IReason {
  Reason:string;
  Value:string;
  ListIndex?:number;
}
export interface ITreeListStyleIcon extends IMapLayerIcon{
};
export interface ITreeList extends IMapLayer {
  code?: string;
  url?: string;
}

/**
 * for IUser. Must be the save value as defined in appgis.user_group
 */
export enum EUserGroup {
  Administrators = 1,
  ApplicationSupports,
  Executives = 10,
  Governors = 11,
  Manager = 20,
  Directors,
  DivisionManagers,
  SectionChiefs,
  TeamLeads,
  TeamMembers,
  Operators= 50,
  OperatorsOthers = 51,
  Citizens = 91,
  BusinessMen,
  Specialists
};

export enum EUserStatus {
  WaitingToConfirm = 1,
  Active = 2,
  Revoked = -1
}
export enum EStickType {
  Free = 1,
  Purchased,
  Favorite
}

/**
 * for IContact
 */
export enum ETeamType {
  Official = 1,
  Group,
  Individual
}
export enum EContactStatus {
  Invited = 1,
  Added,
  Blocked
}

/**
 * for chat
 */
export enum EChannelType {
  Public = 1,
  Private,
  Group, 
  Direct
}; 
export enum EMessageType {
  Text = 1,
  Html,
  Image,
  Video,
  Sticker,
  File,
  Location //contains lat/lon to be displayed on map
};

/**
 * for request
 */

export enum EIntention {
  AskForService = 1,
  Report,
  MakeComplaint,
  GiveOpinion
}
export enum ERequestReason {
  ReasonA = 1,
  ReasonB,
  ReasonC,
  Others = -1
}
export enum ERequestStatus {
  Draft = 0,
  Sent = 1,
  //New = 1,
  Returned,
  Refused,
  Accepted,
  Assigned,
  Preparing,
  Implementing,
  Done,
  Closed
}

export enum ERequestPrivacy {
  Public = 1,
  Private
};

export enum EParagraphType {
  Cancelled = -1,
  Text = 1,
  Image,
  Video,
  Location //contains lat/lon to be displayed on map
};

export enum ENotificationType {
  General = 1,
  Schedule
}

// for chat
export interface IUserNotifyProps {
  /**
   * Set to "true" to enable email notifications, "false" to disable. Defaults to "true"
   */
  email?: string;
  /**
   * Set to "all" to receive push notifications for all activity, "mention" for mentions and direct messages only, and "none" to disable. Defaults to "mention"
   */
  push?: string;
  /**
   * Set to "all" to receive desktop notifications for all activity, "mention" for mentions and direct messages only, and "none" to disable. Defaults to "all"
   */
  desktop?: string;
  /**
   * Set to "true" to enable sound on desktop notifications, "false" to disable. Defaults to "true"
   */
  desktop_sound?: string;
  /**
   * A comma-separated list of words to count as mentions. Defaults to username and @username
   */
  mention_keys?: string;
  /**
   * Set to "true" to enable channel-wide notifications (@channel, @all, etc.), "false" to disable. Defaults to "true"
   */
  channel?: string;
  /**
   * Set to "true" to enable mentions for first name. Defaults to "true" if a first name is set, "false" otherwise
   */
  first_name?: string;
}

export interface IUserProps {
  /**
   * Thai 13 digit ID, required
   */
  hallo_id?: string;
  ssn?: string;
  passport_no?: string;
  prefix?: string;
  mobile?: string;
  caption?: string;
  gender?: "male" | "female";
  birthday?: number;
  country_name?:string;
  country_code?:string;
  country_idc?:string;
  addr_no?: string;
  addr_moo?: string;
  addr_alley?: string;
  addr_street?: string;
  addr_village?: string;
  addr_town?: string;
  addr_county?: string;
  addr_province?: string;
  addr_country?: string;
  addr_zipcode?: string;
  //settings?: ISettings;
  /**
   * User group/roles, required
   */
  ugroup?: string;
  //ustatus?: string;
  /**
   * deleteat>0 then -1 else emailverified then 2 else 1
   */

  org_code?: string;
  superordinate?: string; //Superordinate HalloId
  subordinates?: string; //Comma separated User.HalloId
  aor_table?: string;
  aor_column?: string;
  aor_code?: string;
  level?: number;
}

export interface IUserProperties {
  id?: string;
  /**
   * dateTime: milliseconds: epoch multiplied by 1000
   */
  create_at?: number;
  update_at?: number;
  delete_at?: number;
  last_password_update?: number;
  last_picture_update?: number;
  failed_attempts?: number;
  mfa_active?: boolean;
  /**
   * User name/ID, required
   */
  username?: string;
  first_name?: string;
  last_name?: string;
  nickname?: string;

  /**
   * User email, required
   */
  email?: string;
  email_verified?: boolean;
  /**
   * User password, required
   */
  password?: string;
  auth_service?: string;
  roles?: string;
  locale?: string;

  position?: string;
  props?: IUserProps;
  notify_props?: IUserNotifyProps
}

export interface ITeamProperties {
  id: string;
  create_at: number;
  update_at: number;
  delete_at: number;
  display_name: string;
  name: string;
  description: string;
  email: string; //determine the owner of this team
  type: string; //O:open|I:invited only
  allowed_domains: string;
  invite_id: string;
  allow_open_invite: boolean;
}

export interface IPostProperties {
  id?: string;
  create_at?: number;
  update_at?: number;
  delete_at?: number;
  edit_at?: number;
  user_id?: string;
  channel_id?: string;
  root_id?: string;
  parent_id?: string;
  original_id?: string;
  message?: string;
  type?: string;
  props?: {[key:string] : any};
  hashtag?: string;
  filenames?: string[];
  file_ids?: string[]
  pending_post_id?: string
}

export interface IFileProperties {
  id?: string;
  user_id?: string;
  post_id?: string;
  create_at?: number;
  update_at?: number;
  delete_at?: number;
  name?: string;
  extension?: string;
  size?: number;
  mime_type?: string;
  width?: number;
  height?: number;
  has_preview_image?: boolean
}

export interface IUploadFile {
  file?: File;
  url?: any; //Exif.createImageURL()
  index?: number;
  name?: string;
  thumb?: string;
  size?: number;
  speed?: number;
  uploaded?: boolean;
  uploading?: boolean;
  progress?: number;
  status?: string;
  error?: string;
  orientation?: number;
  width?: number;
  height?: number;
  date?: Date;
  longitude?: number;
  latitude?: number;
  hasCoordinate?: boolean;
  uploader?: any; // default to use axios rather fetch. axios supports onProgress while fetch doesn't
  cancel?: any; // axios CancelToken
  description?: string;
  properties?: {[key:string] : any};
}

export interface IUnread {
  team_id?: string;
  channel_id?: string;
  msg_count?: number;
  mention_count: number;
}

export interface IReportable {
  level?: string;
  title?: string;
  name?: string; // RESTful resource name
  columns?: {[key:string] : any} [];
  rows?: {[key:string] : any} [];
  sumRow?: {[key:string] : any};
  expandedRow?: {[key:string] : any};
  expanded?: boolean;
  expandedRowIndex?: number;
  child?: IReportable; //sub report
}

export interface IIconTool {
  id?: number;
  name?: string;
  divider?: boolean;
  title?: string;
  description?: string;
  icon?: string;
  icon2?: string;
  color?: string;
  class?: string;
  replacer?: any[];
  allowed?: Function;
  nameReplacer?: any[];
  subTitle?: string | Date;
  subTitleIsDate?: boolean;
}

export interface IIconMenu extends IIconTool {
  url?: string;
  requireLogin?: boolean;
  submenus?: IIconMenu[];
}

export interface IUserAuditProperties {
  id?: string;
  create_at?: number;
  user_id?: string;
  action?: string;
  extra_info?: string;
  ip_address?: string;
  session_id?: string;
}

export interface IFileGaf extends IBase {
  Title: string;
  Description: string;
  Dates: ITransactionDates;
  Creator: IUser;
  Author: IUser;
}

export interface ITimeRange {
  start: Date;
  end: Date;
}